import { expenseStore } from "../expenses";
import { invoiceStore } from "../invoices";
import { categoryStore } from "../categories";
import { taxPolicyStore } from "../taxPolicies";
import { receiptStore } from "../receipts";
import { buildExpensesCsv, buildExportFilename } from "./csv";
import {
  generateExpensesPdf,
  filterExpensesForExport,
} from "./pdf";
import { buildAuditPackage } from "./zipBundle";
import type {
  ExportRecord,
  ExportRequest,
  ExportStore,
  ExportDataSources,
} from "./types";

const STORAGE_KEY = "proofly.exports.v1";
// Well-known synthetic user id in bypass mode; mirrors the one the
// expense/invoice stores use so storage paths look like the real server.
const BYPASS_USER_ID = "00000000-0000-0000-0000-000000000000";

function readFromStorage(): ExportRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as ExportRecord[];
  } catch {
    return [];
  }
}

function writeToStorage(records: ExportRecord[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `x_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function triggerDownloadFromBlob(blob: Blob, filename: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  // Safari requires the anchor to be connected to the DOM before a click is
  // registered. Append, click, then remove synchronously so no stray node is
  // left around.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick — some browsers abort the download if we revoke
  // synchronously right after click().
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function triggerBrowserDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  triggerDownloadFromBlob(blob, filename);
}

async function blobToBase64(blob: Blob): Promise<string> {
  // FileReader is the cheapest way to get to base64 in a browser without
  // pulling in a polyfill. `result` is a `data:<mime>;base64,<payload>`
  // string; we slice off the prefix.
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onerror = () => {
      reject(reader.error ?? new Error("FileReader failed"));
    };
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

function collectSources(): ExportDataSources {
  return {
    expenses: expenseStore.list(),
    invoices: invoiceStore.list(),
    categories: categoryStore.list().map((c) => ({ id: c.id, label: c.label })),
    taxPolicies: taxPolicyStore
      .list()
      .map((p) => ({ id: p.id, category: p.category })),
    receipts: receiptStore.list().map((r) => ({
      id: r.id,
      linkedExpenseId: r.linkedExpenseId,
      linkedInvoiceId: r.linkedInvoiceId,
    })),
  };
}

export function createLocalStorageExportStore(): ExportStore {
  const listeners = new Set<() => void>();

  // Stable snapshot reference — same rule as every other store in the repo
  // (useSyncExternalStore's Object.is would re-fire on every render
  // otherwise).
  let snapshot: ExportRecord[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function commit(next: ExportRecord[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  return {
    list(): ExportRecord[] {
      return snapshot;
    },

    async create(req: ExportRequest): Promise<ExportRecord> {
      const sources = collectSources();
      const createdAt = new Date();
      const id = generateId();

      if (req.format === "zip") {
        // Audit package: CSV + PDF + raw receipts. We pull the full
        // receipts (not just the projection in `sources`) so we have access
        // to filename, mimeType, and url for the bundle.
        const fullReceipts = receiptStore.list();
        const result = await buildAuditPackage(sources, req, fullReceipts);
        const blob = new Blob([result.zipBytes as BlobPart], {
          type: "application/zip",
        });
        const filename = buildExportFilename("zip", createdAt);
        const contentBase64 = await blobToBase64(blob);
        const record: ExportRecord = {
          id,
          filename,
          format: "zip",
          rowCount: result.rowCount,
          filterJson: { ...req, _bundledReceipts: result.receiptCount },
          createdAt: createdAt.toISOString(),
          mimeType: "application/zip",
          contentBase64,
          sizeBytes: blob.size,
          storagePath: `${BYPASS_USER_ID}/${id}.zip`,
        };
        triggerDownloadFromBlob(blob, filename);
        commit([record, ...snapshot]);
        return record;
      }

      if (req.format === "pdf") {
        // PDF path: generate via @react-pdf/renderer, persist the bytes as
        // base64 so the history "Download" button can replay without going
        // back to live data.
        const filteredExpenses = filterExpensesForExport(sources.expenses, {
          dateFrom: req.dateFrom,
          dateTo: req.dateTo,
          categoryIds: req.categoryIds,
          userId: req.userId ?? null,
        });
        const blob = await generateExpensesPdf({
          expenses: filteredExpenses,
          categories: sources.categories,
          taxPolicies: sources.taxPolicies,
          orgName: "Proofly",
          dateRange: { from: req.dateFrom, to: req.dateTo },
        });
        const filename = buildExportFilename("pdf", createdAt);
        const contentBase64 = await blobToBase64(blob);
        const record: ExportRecord = {
          id,
          filename,
          format: "pdf",
          rowCount: filteredExpenses.length,
          filterJson: { ...req },
          createdAt: createdAt.toISOString(),
          mimeType: "application/pdf",
          contentBase64,
          sizeBytes: blob.size,
          storagePath: `${BYPASS_USER_ID}/${id}.pdf`,
        };
        triggerDownloadFromBlob(blob, filename);
        commit([record, ...snapshot]);
        return record;
      }

      // CSV path (unchanged behaviour — keep the historical download type
      // and persisted `content` string so existing tests that inspect file
      // contents still pass).
      const { content, rowCount } = buildExpensesCsv(sources, req);
      const filename = buildExportFilename("csv", createdAt);
      const record: ExportRecord = {
        id,
        filename,
        format: "csv",
        rowCount,
        filterJson: { ...req },
        createdAt: createdAt.toISOString(),
        content,
        sizeBytes: new Blob([content]).size,
        mimeType: "text/csv",
        storagePath: `${BYPASS_USER_ID}/${id}.csv`,
      };
      triggerBrowserDownload(content, filename);
      commit([record, ...snapshot]);
      return record;
    },

    async remove(id: string): Promise<void> {
      const next = snapshot.filter((r) => r.id !== id);
      if (next.length !== snapshot.length) commit(next);
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// Helper exported for the page: re-trigger a download from a persisted
// record. Kept here (not on the store) because the store API is kept
// minimal; the page owns the "re-download this history row" interaction.
export function redownloadExportRecord(record: ExportRecord): void {
  // Binary formats (PDF): replay the stored bytes directly.
  if (record.contentBase64 && record.mimeType) {
    const blob = base64ToBlob(record.contentBase64, record.mimeType);
    triggerDownloadFromBlob(blob, record.filename);
    return;
  }
  if (record.content) {
    triggerBrowserDownload(record.content, record.filename);
    return;
  }
  // Fallback: re-generate from live data using the stored filter. This is
  // the path the Supabase store relies on for CSV records.
  const sources = collectSources();
  const req: ExportRequest = {
    format: record.format,
    dateFrom: (record.filterJson.dateFrom as string | undefined) ?? null,
    dateTo: (record.filterJson.dateTo as string | undefined) ?? null,
    categoryIds: record.filterJson.categoryIds as string[] | undefined,
    includeReceipts: Boolean(record.filterJson.includeReceipts),
    includeNotes: Boolean(record.filterJson.includeNotes),
    userId: (record.filterJson.userId as string | undefined) ?? null,
  };
  const { content } = buildExpensesCsv(sources, req);
  triggerBrowserDownload(content, record.filename);
}

// Testing hook: return the Blob for a stored export so Playwright can
// inspect bytes (PDF magic number) without going through the browser
// download event. Kept here because the logic to reconstruct the blob from
// base64 already lives in this file.
export function blobFromExportRecord(record: ExportRecord): Blob | null {
  if (record.contentBase64 && record.mimeType) {
    return base64ToBlob(record.contentBase64, record.mimeType);
  }
  if (record.content) {
    return new Blob([record.content], {
      type: record.mimeType ?? "text/csv;charset=utf-8",
    });
  }
  return null;
}
